import { FormikHelpers } from "formik";
import React, { FC, useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ErrorFace } from "@/components/ErrorFace";
import { applyApiErrors } from "@/components/Formik";
import { IInvitation } from "@/data/invitations";
import { MessagesContext } from "@/eworkitComponents";
import { Flex, Header, Text } from "@/eworkitComponents";
import { useFetcher } from "@/hooks/http";
import { RouteButton, withRouteProps } from "@/infrastructure/Route";
import { FORM_MESSENGER_CONTEXT, ISignUpUserFormValues } from "@/views/components/Forms/SignUpUserForm";
import { AnimatedLayoutContainer } from "@/views/components/Layout";
import { router } from "@/views/router";
import { SessionLogin, useLinkProfileAfterInvitation } from "@/views/Session/Login";

import { InvitationAccountPicker } from "./InvitationAccountPicker";
import { InvitationSignUpFormik } from "./InvitationSignUpForm";
import { transformAcceptRouteProps } from "./selectors";
import { IInvitationDetailRouteProps, InvitationAccountType } from "./types";

interface IRegisterInvitationDto {
  token: string;
}

interface IInvitationsAcceptComponentProps extends IInvitationDetailRouteProps {}

export const buildRegisterInvitation = (
  fetchInvitation: (params: { invitationId: number; token: string }) => Promise<IInvitation>,
  registerInvitation: (params: { invitationId: number }, payload: IRegisterInvitationDto) => Promise<boolean>,
  acceptedTextKey: string,
) => {
  const InvitationsAcceptComponent: FC<IInvitationsAcceptComponentProps> = props => {
    const Messages = useContext(MessagesContext);

    const { t } = useTranslation(["public", "common"]);

    const [accepted, setAccepted] = useState(false);
    const [accountType, setAccountType] = useState<InvitationAccountType>();

    const { invitationId, token } = props.routeProps;
    const fetcher = useFetcher(fetchInvitation, { invitationId, token });

    const linkProfileAfterInvitation = useLinkProfileAfterInvitation();

    const handleNewAccountCancel = useCallback(() => {
      setAccountType(undefined);
    }, [setAccountType]);

    useEffect(() => {
      if (!fetcher.payload) {
        return;
      }

      if (fetcher.payload.status === "ACCEPTED") {
        setAccepted(true);
      }

      if (fetcher.payload.alreadyExist) {
        props.history.push(router.get("login").path, fetcher.payload);
      }
    }, [fetcher.payload]);

    const handleSignUpSubmit = async (values: ISignUpUserFormValues, actions: FormikHelpers<ISignUpUserFormValues>) => {
      try {
        await registerInvitation({ invitationId: (fetcher.payload as IInvitation).id }, { ...values, token });
        setAccepted(true);
      } catch (response) {
        Messages.addMessage({
          text: t("common:VALIDATION_MESSAGES.GENERAL"),
          type: "warning",
          context: FORM_MESSENGER_CONTEXT,
        });
        applyApiErrors(actions, response.payload);
        actions.setSubmitting(false);
      }
    };

    const linkProfile = async () => {
      return await linkProfileAfterInvitation(invitationId, token);
    };

    const renderError = () => {
      return (
        <AnimatedLayoutContainer size="lg" showLogo>
          <Flex margin={["xl"]} innerMargin="md" direction="column" align="center">
            <ErrorFace />
            <div>
              <Header size="sm" align="center">
                {t("SESSION_INVITATION.EXPIRED_INCORRECT_LINK_TEXT")}
              </Header>
            </div>
            <div>
              <Flex margin={["xl", false, false, false]}>
                <RouteButton size="lg" path={router.get("login").path} testName={router.get("login").name}>
                  <Text>{t("SESSION_INVITATION.BUTTON_LOGIN")}</Text>
                </RouteButton>
              </Flex>
            </div>
          </Flex>
        </AnimatedLayoutContainer>
      );
    };

    const renderAccepted = () => {
      return (
        <AnimatedLayoutContainer size="lg" showLogo>
          <Flex margin={["xl"]} innerMargin="md" direction="column" align="center">
            <Header color="primary" size="sm" text={t("SESSION_INVITATION.ACCEPTED_HEADER")} align="center" />
            <div>
              <Header color="primary" size="sm" text={t(acceptedTextKey)} align="center" />
            </div>
            <div>
              <Flex margin={["xl", false, false, false]}>
                <RouteButton
                  size="lg"
                  path={router.get("login").path}
                  state={{ email: fetcher.payload && fetcher.payload.email }}
                  testName={router.get("login").name}>
                  <Text>{t("SESSION_INVITATION.BUTTON_LOGIN")}</Text>
                </RouteButton>
              </Flex>
            </div>
          </Flex>
        </AnimatedLayoutContainer>
      );
    };

    if (fetcher.error) {
      return renderError();
    }

    if (accepted) {
      return renderAccepted();
    }

    if (!fetcher.payload) {
      return null;
    }

    if (fetcher.payload.alreadyExist) {
      return null; // because of redirect to login in useEffect
    }

    if (accountType === "new") {
      return (
        <InvitationSignUpFormik
          invitation={fetcher.payload}
          onSubmit={handleSignUpSubmit}
          onCancel={handleNewAccountCancel}
        />
      );
    }

    if (accountType === "existing") {
      return (
        <AnimatedLayoutContainer size="sm" showLogo>
          <SessionLogin
            linkProfile={linkProfile}
            oAuthRedirectUriQueryString={`invitation_id=${invitationId}&invitation_token=${token}`}
          />
        </AnimatedLayoutContainer>
      );
    }

    return <InvitationAccountPicker onAccountTypeSelected={setAccountType} />;
  };

  return withRouteProps(InvitationsAcceptComponent, transformAcceptRouteProps);
};
